import { CircularGallery, type GalleryItem } from '~/components/circular-gallery-2';
import { useEffect, useRef, useState } from 'react';

// Helper to draw the card background and content
const drawCard = (
  ctx: CanvasRenderingContext2D,
  info: { width: number; height: number; index: number },
  item: { image: string; text: string; cardBackgroundColor: string },
) => {
  const { width, height } = info;

  // Calculate responsive scale based on a reference width of 500px (Desktop)
  const scale = width / 500;

  // Scaled values
  const padding = Math.floor(80 * scale);
  const titleFontSize = Math.floor(54 * scale);
  const startX = Math.floor(100 * scale);
  const startY = Math.floor(180 * scale);
  const innerRadius = Math.floor(32 * scale);

  const radius = 20; // Keep main radius constant or scale slightly if preferred
  const titleHeight = titleFontSize * 1.5; // Estimate line height

  // 1. Draw Card Background
  ctx.fillStyle = item.cardBackgroundColor; // Teal background
  ctx.beginPath();

  if (ctx.roundRect) {
    ctx.roundRect(0, 0, width, height, radius);
  } else {
    ctx.rect(0, 0, width, height);
  }

  ctx.fill();

  // 2. Draw Title
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${titleFontSize}px "Inter Display", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(item.text, width / 2, padding + titleHeight / 2);

  // 3. Draw Image with Diagonal Cut
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = item.image;

  return new Promise<void>((resolve) => {
    img.onload = () => {
      // Define the "Window" area for the image
      const drawW = width - startX; // Go to right edge
      const drawH = height - startY; // Go to bottom edge

      ctx.save();

      // -- Clipping Path --
      // We want to clip to the card's rounded corners first (to ensure bottom-right is round)
      // Actually, we can rely on the fact that we draw *over* the card background?
      // No, we need to ensure the image doesn't bleed out of the card's rounded corners.
      // So first clip to the Card Boundary.
      ctx.beginPath();
      ctx.roundRect(0, 0, width, height, radius);
      ctx.clip(); // Clip 1: Card Shape

      // Now define the Image Shape (Top-Left Rounded)
      ctx.beginPath();
      ctx.moveTo(startX + innerRadius, startY);
      ctx.lineTo(startX + drawW, startY); // Top edge
      ctx.lineTo(startX + drawW, startY + drawH); // Right edge
      ctx.lineTo(startX, startY + drawH); // Bottom edge
      ctx.lineTo(startX, startY + innerRadius); // Left edge
      ctx.arcTo(startX, startY, startX + innerRadius, startY, innerRadius); // Top-Left Curve
      ctx.closePath();

      ctx.clip(); // Clip 2: Image Shape

      // -- Draw Image --
      // "Cover" logic aligned to Top-Left
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const areaAspect = drawW / drawH;

      let renderW, renderH;

      if (imgAspect > areaAspect) {
        // Image is wider than area -> Height fits, Width cropped
        renderH = drawH;
        renderW = drawH * imgAspect;
      } else {
        // Image is taller than area -> Width fits, Height cropped
        renderW = drawW;
        renderH = drawW / imgAspect;
      }

      // Draw at (startX, startY)
      ctx.drawImage(img, startX, startY, renderW, renderH);

      ctx.restore();
      resolve();
    };

    img.onerror = () => {
      console.warn('Failed to load image for card:', item.image);
      resolve();
    };
  });
};

// Define gallery items with custom draw logic
const galleryItems: GalleryItem[] = [
  {
    image: '/images/featured-dashboards.png',
    text: 'Dashboards',
    cardBackgroundColor: '#C96565',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-dashboards.avif',
        text: 'Dashboards',
        cardBackgroundColor: '#C96565',
      }),
  },
  {
    image: '/images/featured-ecommerce.png',
    text: 'E-commerce',
    cardBackgroundColor: '#3A5AA1',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-e-commerce.avif',
        text: 'E-commerce',
        cardBackgroundColor: '#3A5AA1',
      }),
  },
  {
    image: '/images/featured-landing-page.png',
    text: 'Landing Page',
    cardBackgroundColor: '#266D65',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-landing-page.avif',
        text: 'Landing Page',
        cardBackgroundColor: '#266D65',
      }),
  },
  {
    image: '/images/featured-mobile-apps.png',
    text: 'Mobile Apps',
    cardBackgroundColor: '#E49351',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-mobile-apps.avif',
        text: 'Mobile Apps',
        cardBackgroundColor: '#E49351',
      }),
  },
  {
    image: '/images/featured-portfolio.png',
    text: 'Portfolio',
    cardBackgroundColor: '#DFBC91',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-portfolio.avif',
        text: 'Portfolio',
        cardBackgroundColor: '#DFBC91',
      }),
  },
  {
    image: '/images/featured-productivity.png',
    text: 'Productivity',
    cardBackgroundColor: '#1976D2',
    onDraw: (ctx, info) =>
      drawCard(ctx, info, {
        image: '/images/featured-productivity.avif',
        text: 'Productivity',
        cardBackgroundColor: '#1976D2',
      }),
  },
];

// Simplified vertex shader for mobile (no wave distortion)
const mobileVertexShader = `
  precision highp float;
  attribute vec3 position;
  attribute vec2 uv;
  uniform mat4 modelViewMatrix;
  uniform mat4 projectionMatrix;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export default function FeatureShowcaseNew() {
  const [layout, setLayout] = useState({
    width: 500,
    height: 800,
    bend: 1.5,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return () => {
        return undefined;
      };
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: containerWidth } = entry.contentRect;

        // --- Hybrid Scaling Logic (Discrete + Linear Interpolation) ---
        let targetWidth;
        let targetBend;

        if (containerWidth < 640) {
          // Mobile Zone: 300px -> 640px
          // Width: Smoothly shrink ratio from 85% to 70% as screen gets wider
          const t = (containerWidth - 300) / (640 - 300);
          const clampedT = Math.min(1, Math.max(0, t));
          const ratio = 0.85 - 0.15 * clampedT; // 0.85 -> 0.70

          targetWidth = Math.max(280, containerWidth * ratio);
          targetBend = 0.1 + 0.1 * clampedT; // 0.1 -> 0.2 (Slight curve increase)
        } else if (containerWidth < 1024) {
          // Tablet Zone: 640px -> 1024px
          // Width: Ratio from 60% -> 45%
          const t = (containerWidth - 640) / (1024 - 640);
          const ratio = 0.6 - 0.15 * t;

          targetWidth = containerWidth * ratio;
          targetBend = 0.2 + 0.8 * t; // 0.2 -> 1.0 (Curve ramps up)
        } else {
          // Desktop Zone: > 1024px
          // Cap width, constant bend
          targetWidth = Math.min(500, containerWidth * 0.35);
          targetBend = 1.5;
        }

        // Height is always proportional
        const targetHeight = targetWidth * 1.6;

        setLayout({
          width: targetWidth,
          height: targetHeight,
          bend: targetBend,
        });
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#10110C]">
      {/* Background Image - you'll replace this with your actual starry sky/clouds background */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/starry-background.avif" // Replace with your actual background image
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Content Container - Ref attached here for sizing source */}
      <div ref={containerRef} className="relative z-10 w-full h-full pt-4 sm:pt-12 lg:pt-16 flex flex-col">
        {/* Heading Section */}
        <div className="absolute top-[15%] xs:top-[25%] sm:top-[20%] md:top-[15%] lg:top-[15%] xl:top-[10%] left-1/2 -translate-x-1/2 text-center w-full flex-shrink-0 z-50 px-4">
          <h2 className="text-white mb-4 lg:mb-6 text-3xl sm:text-4xl lg:text-5xl xl:text-[64px] leading-[120%] text-center">
            <span className="font-semibold" style={{ fontFamily: 'Inter Display' }}>
              You can build{' '}
            </span>
            <span className="font-normal italic" style={{ fontFamily: 'Instrument Serif' }}>
              literally anything
            </span>
          </h2>
          <p
            className="text-white/80 text-base sm:text-lg lg:text-xl xl:text-[28px] xl:leading-[28px] font-medium max-w-3xl mx-auto text-center"
            style={{ fontFamily: 'Inter Display' }}
          >
            Stop wasting money on buying tokens. Shift to Dualite'.
          </p>
        </div>

        {/* Circular Gallery Carousel Component */}
        <div className="w-full flex-1 min-h-0">
          <CircularGallery
            items={galleryItems}
            bend={layout.bend}
            borderRadius={0.05}
            scrollSpeed={2}
            scrollEase={0.05}
            baseItemHeight={layout.height}
            baseItemWidth={layout.width}
            className="w-full h-full"
            fontClassName="font-semibold text-[30px]"
            vertexShader={undefined}
          />
        </div>
      </div>
    </div>
  );
}
